import { z } from 'zod';

/**
 * A company timezone, as written: an IANA zone this runtime's `Intl` resolves.
 *
 * The column used to take any string (`z.string()`), so a free-text value from
 * the old install wizard — "GMT+4", "Dubai", "UTC+4" — was stored, passed every
 * read, and then made `fromZonedTime` return an Invalid Date that Prisma
 * rejected with a 500 on the record screens, while the rollup quietly paid on a
 * fallback zone (audit 2026-10-15, M-45). The readers now sanitise
 * (`safeTimeZone`); this stops the value at the door, so the fallback is a
 * legacy row's problem and never a new one's.
 *
 * `Intl.DateTimeFormat` with the zone is the test the readers use, so the
 * writer and the readers cannot disagree about what is valid.
 */
export function isIanaTimeZone(tz: string): boolean {
  if (tz === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const IanaTimeZoneSchema = z
  .string()
  .refine(isIanaTimeZone, { message: 'Not a recognised IANA time zone (e.g. Asia/Dubai)' });

/**
 * The company's currency: three letters after trimming and upper-casing
 * (ISO 4217 shape). It is printed beside every figure on a payslip and a
 * letter, so "aed " is stored as "AED" and "Dirham" is refused with a 422 —
 * the same rule the Company tab applies before it sends (owner, 2026-10-21).
 */
export const CURRENCY_CODE_RE = /^[A-Z]{3}$/;
export const CurrencyCodeSchema = z
  .string()
  .transform((s) => s.trim().toUpperCase())
  .pipe(z.string().regex(CURRENCY_CODE_RE, 'Currency must be a three-letter code (e.g. AED)'));

/* THE WEEK NO LONGER STARTS THE DAY AFTER THE WEEKEND. `weekStartFromWeekend`
 * (audit L-13) derived it from `weekendDays` — Sunday under the default Fri/Sat
 * weekend — while the roster, the field app and the weekly report used Monday.
 * Owner, 2026-10-31: the company week starts on Monday, one rule for all of
 * them (`../companyWeek.ts`, audit MT-05). `weekendDays` still decides which
 * days are rest days. */
